import { Link } from "react-router-dom";

export default function PrivacyPage() {
  const updated = "May 10, 2026";
  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4 sm:px-8" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto bg-white border border-zinc-200 rounded-sm p-6 sm:p-10">
        <Link to="/" className="text-xs text-[#002FA7] hover:underline">&larr; Back to Expenser</Link>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-900 mt-3" style={{ fontFamily: "DM Sans, sans-serif" }}>
          Privacy Policy
        </h1>
        <p className="text-xs text-zinc-400 mt-1">Last updated: {updated}</p>

        <div className="prose prose-sm mt-6 text-zinc-700 leading-6 space-y-4">
          <p>
            C3 Contracting, LLC ("we", "us") operates the Expenser application ("Application"). This Privacy Policy explains what
            information we collect, how we use it, and the choices you have. By using the Application you agree to this Policy.
          </p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">1. Information We Collect</h2>
          <p>We collect the minimum information needed to operate the Application:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Account data</strong>: name, work email, role, and a hashed password.</li>
            <li><strong>Expense data</strong>: vendor, amount, date, category, GL account, job, budget item, splits, receipts, and notes that you create or submit.</li>
            <li><strong>Card transaction data</strong>: card identifiers, last four digits, merchant, amount, date, and posting status received from Mercury Bank for cards mapped to users in your organization.</li>
            <li><strong>Integration tokens</strong>: OAuth access and refresh tokens for QuickBooks Online and Mercury, plus API keys you supply for JobTread. These are stored encrypted in our database and never shown in full again.</li>
            <li><strong>Usage data</strong>: server logs containing IP address, request timestamps, and endpoints accessed for security and troubleshooting.</li>
          </ul>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">2. How We Use Your Information</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>To authenticate users and authorize access</li>
            <li>To capture, code, approve, and synchronize expense data with the integrations you have configured</li>
            <li>To send in-app and push notifications for new card swipes, approvals, and rejections</li>
            <li>To detect and prevent fraud or abuse, and to comply with legal obligations</li>
          </ul>
          <p>We do not sell or share your data with third parties for advertising.</p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">3. Third-Party Integrations</h2>
          <p>
            When you connect QuickBooks Online, JobTread, or Mercury Bank, the Application exchanges data only with those services
            on your behalf using credentials you provide. You can disconnect any integration at any time through the Settings page,
            which will revoke our stored tokens and stop further data exchange. We do not retain copies of data after disconnection
            beyond what is required by law.
          </p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">4. QuickBooks Data</h2>
          <p>
            We request the minimum QuickBooks Online scope (<span className="font-mono">com.intuit.quickbooks.accounting</span>)
            necessary to sync vendors, GL accounts, customers, classes, and Purchase entities. We do not request, store, or transmit
            payroll, sensitive HR, or banking data from QuickBooks.
          </p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">5. Data Security</h2>
          <p>
            All data is transmitted over HTTPS (TLS 1.2+). Passwords are hashed using bcrypt. OAuth and API secrets are stored
            encrypted at rest. Access to production systems is restricted to authorized administrators with multi-factor
            authentication. Webhooks from Mercury are validated using HMAC-SHA256 signatures. OAuth flows use CSRF state tokens.
          </p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">6. Data Retention</h2>
          <p>
            We retain expense and transaction data for as long as your account is active and for up to seven (7) years thereafter
            for tax and audit purposes, after which it is deleted. You may request earlier deletion by contacting us, subject to
            legal hold requirements.
          </p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">7. Your Rights</h2>
          <p>
            You may access, correct, export, or delete your personal data at any time by emailing the contact below. Admins of your
            organization may also manage user accounts directly in Settings.
          </p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">8. Children's Privacy</h2>
          <p>The Application is not intended for individuals under 18 and we do not knowingly collect data from minors.</p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">9. Changes to This Policy</h2>
          <p>
            We may update this Policy from time to time. Material changes will be announced via in-app notification or email at
            least 30 days before they take effect.
          </p>

          <h2 className="text-lg font-semibold text-zinc-900 pt-3">10. Contact</h2>
          <p>
            For privacy questions, please email <a href="mailto:daniel.wayne@c3-contracting.com" className="text-[#002FA7] hover:underline">daniel.wayne@c3-contracting.com</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
